import type { MetaResponse, PayoutSetupResponse, PayoutStatusResponse } from '@league/shared/contract';
import { LINK_CLASS } from '@league/ui';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button, ErrorNote } from '../../components/ui';
import { apiFetch, apiPost } from '../../lib/api';

/**
 * "PLAYER PAYMENTS" (owner, 2026-10-06: the heading) — her own Stripe account, so her players can pay through the platform and the
 * money goes straight to her bank (owner, 2026-10-06). Shown only where player payments are open.
 *
 * Four states: not started, started but Stripe still needs something FROM HER, finished and Stripe
 * is checking, and ready. Stripe's own form does the asking; this page only sends her there and says
 * where she stands. While Stripe checks it asks again every few seconds, so she never has to reload.
 */
export function GetPaidOnline() {
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => apiFetch<MetaResponse>('/api/meta') });
  const open = meta.data?.playerPaymentsOpen === true;
  const status = useQuery({
    queryKey: ['payouts'],
    queryFn: () => apiFetch<PayoutStatusResponse>('/api/billing/payouts'),
    enabled: open,
    refetchInterval: (query) => (query.state.data?.payouts === 'checking' ? 5_000 : false),
  });
  const setup = useMutation({
    mutationFn: () => apiPost<PayoutSetupResponse>('/api/billing/payouts/setup', {}),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  if (!open) return null;
  const state = status.data?.payouts;
  /* How linking her Stripe account went, as Stripe's sign-in sends her back (2026-10-29). */
  const returned = new URLSearchParams(window.location.search).get('payouts');
  const linkProblem =
    state === 'ready' || state === 'checking'
      ? null
      : returned === 'different'
        ? 'A different Stripe account is already linked.'
        : returned === 'failed'
          ? 'Stripe could not link that account. Try again.'
          : null;

  return (
    <section>
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">Player payments</h2>
      <div className="mt-3 flex flex-col gap-3 rounded-md border border-light bg-paper px-5 py-4">
        {status.isPending ? (
          <p className="text-[14px] text-ink-2">Loading…</p>
        ) : state === 'checking' ? (
          <p className="text-[14px]">Stripe is checking your details. This usually takes a minute or two.</p>
        ) : state === 'ready' ? (
          <>
            <p className="text-[14px] font-medium">Ready to take payments.</p>
            <p className="text-[13px] text-ink-2">Payouts, refunds and receipts are in your Stripe account.</p>
            <a className={`w-fit text-[14px] ${LINK_CLASS}`} href="https://dashboard.stripe.com" target="_blank" rel="noreferrer">
              Open Stripe
            </a>
          </>
        ) : (
          <>
            {/* Money she will not see otherwise: who takes the fee is said once, here. */}
            <p className="max-w-[34rem] text-[14px] leading-relaxed">
              {state === 'incomplete'
                ? 'Stripe still needs a few details before you can take payments.'
                : 'Let players pay when they register. Payments go to your own Stripe account; Stripe’s fee (2.9% + 30¢) comes out of each one.'}
            </p>
            <div>
              <Button disabled={setup.isPending} onClick={() => setup.mutate()}>
                {setup.isPending ? 'Opening Stripe…' : state === 'incomplete' ? 'Finish setting up' : 'Set up payments'}
              </Button>
            </div>
          </>
        )}
        {linkProblem && !setup.isError ? <ErrorNote>{linkProblem}</ErrorNote> : null}
        {setup.isError ? <ErrorNote>{setup.error instanceof Error ? setup.error.message : 'Stripe could not be reached.'}</ErrorNote> : null}
      </div>
    </section>
  );
}
